import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Send, MessageCircle, Linkedin, Facebook, CheckCircle } from 'lucide-react';
import SEO from '../components/SEO';
import { PageHero } from '../components/PageComponents';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { contactData } from '../data/mockData';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock submission
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    }, 3000);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <main className="pt-28 min-h-screen bg-gray-50">
      <SEO title="Contact Staffing Force | Kantoor Amsterdam Finance & HR" description="Neem contact op met Staffing Force in Amsterdam voor interim management, werving & selectie of detavast in Finance, HR en Duurzaamheid. Telefoon: (020) 58 11 000." keywords="contact staffing force, Amsterdam, interim, detachering, finance, HR" />

      <PageHero
        label="Neem contact op"
        title="Contact"
        description="Heeft u een vraag of wilt u meer weten over onze dienstverlening? Neem gerust contact met ons op."
        image="https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=1920&q=85&auto=format&fit=crop"
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-2"
          >
            <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Stuur ons een bericht</h2>

              {submitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex flex-col items-center justify-center py-16"
                >
                  <div className="w-16 h-16 rounded-full bg-[#00897B]/10 flex items-center justify-center mb-4">
                    <CheckCircle size={32} className="text-[#00897B]" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Bericht verzonden!</h3>
                  <p className="text-gray-600 text-center">
                    Bedankt voor uw bericht. Wij nemen zo snel mogelijk contact met u op.
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-sm font-medium text-gray-700">Naam *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Uw volledige naam"
                        required
                        className="h-11 border-gray-200"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-sm font-medium text-gray-700">E-mail *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="uw@email.nl"
                        required
                        className="h-11 border-gray-200"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-sm font-medium text-gray-700">Telefoonnummer</Label>
                      <Input
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+31 6 1234 5678"
                        className="h-11 border-gray-200"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">Onderwerp *</Label>
                      <Select
                        value={formData.subject}
                        onValueChange={(val) => setFormData({ ...formData, subject: val })}
                      >
                        <SelectTrigger className="h-11 border-gray-200">
                          <SelectValue placeholder="Selecteer een onderwerp" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="vacature">Vacature informatie</SelectItem>
                          <SelectItem value="opdracht">Opdracht opgeven</SelectItem>
                          <SelectItem value="samenwerking">Samenwerking</SelectItem>
                          <SelectItem value="overig">Overig</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-sm font-medium text-gray-700">Bericht *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Schrijf hier uw bericht..."
                      required
                      rows={6}
                      className="border-gray-200 resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    className="bg-[#00897B] hover:bg-[#00796B] text-white px-8 py-3 rounded font-semibold text-sm tracking-wide transition-all duration-300 hover:shadow-lg hover:shadow-[#00897B]/25 hover:-translate-y-0.5 flex items-center gap-2"
                  >
                    <Send size={16} />
                    Verstuur bericht
                  </button>
                </form>
              )}
            </div>
          </motion.div>

          {/* Contact Info Sidebar */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="space-y-6"
          >
            {/* Office Cards */}
            {contactData.offices.map((office, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
                <h3 className="font-bold text-lg text-gray-900 mb-4">Kantoor {office.city}</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <MapPin size={18} className="text-[#00897B] mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-gray-600">
                      <p>{office.address}</p>
                      <p>{office.postalCode}</p>
                      {office.building && <p>{office.building}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone size={18} className="text-[#00897B] flex-shrink-0" />
                    <a href={`tel:${office.phone}`} className="text-sm text-gray-600 hover:text-[#00897B] transition-colors">
                      {office.phone}
                    </a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail size={18} className="text-[#00897B] flex-shrink-0" />
                    <a href={`mailto:${office.email}`} className="text-sm text-gray-600 hover:text-[#00897B] transition-colors">
                      {office.email}
                    </a>
                  </div>
                </div>
              </div>
            ))}

            {/* Opening Hours */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
              <h3 className="font-bold text-lg text-gray-900 mb-4 flex items-center gap-2">
                <Clock size={18} className="text-[#00897B]" />
                Openingstijden
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Maandag - Vrijdag</span>
                  <span className="font-medium text-gray-900">08:30 - 17:30</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Zaterdag</span>
                  <span className="font-medium text-gray-900">Gesloten</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Zondag</span>
                  <span className="font-medium text-gray-900">Gesloten</span>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="bg-[#00897B] rounded-xl p-6 text-white">
              <h3 className="font-bold text-lg mb-4">Direct contact</h3>
              <p className="text-white/80 text-sm mb-4">
                Neem ook contact op via social media of WhatsApp.
              </p>
              <div className="flex gap-3">
                <a
                  href={`https://wa.me/${contactData.whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
                >
                  <MessageCircle size={18} />
                </a>
                <a
                  href={contactData.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
                >
                  <Linkedin size={18} />
                </a>
                <a
                  href={contactData.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
                >
                  <Facebook size={18} />
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </main>
  );
};

export default ContactPage;
